import { isAdJobMediaType, type CreativeJob, type GenerateMediaType } from '@/store/slices/creativeJobs';

import { ROUTE } from '../../../../routes';
import type { CreativeCarousel } from '../../../creatives/creative-api';
import type { WebsiteCardBody, WebsiteCardIntent, WebsiteCardSummary } from '../../card-summary';

/**
 * What the Website Creatives card says, from what its hook read: the banner pool (whether any banner has a finished
 * regeneration the owner has not opened, `has_new`), each carousel's saved draft against its published order, and
 * whether a banner job of this brand is queued or running. Pure: copy is an id, mapped to its key in
 * `creatives-copy.ts`.
 */

export type ReadStatus = 'loading' | 'failed' | 'ready';

export type CreativesCardInput = {
  /** The `creatives-carousels` flag. */
  carouselsOn: boolean;
  pool: { status: ReadStatus; items: Array<{ hasNew: boolean }> };
  /** One per carousel, in the page's order; `[]` when `carouselsOn` is false. */
  carousels: Array<{ id: CreativeCarousel; status: ReadStatus | 'unavailable'; dirty: boolean }>;
  /** A banner job of this brand is queued or running ({@link bannerJobRunning}). */
  generating: boolean;
};

/**
 * Whether one of `jobs` (the creatives surface jobs) is a banner being drawn for this brand: queued or running, and
 * neither a video run (the video composer owns those), a Banner Kit or Mini-game run (an ad entity job, owned by its
 * composer), nor a `publish`, which the Creatives page shows as "Publishing…" rather than as a banner being generated.
 */
export const bannerJobRunning = (jobs: readonly CreativeJob[], tenantId: number): boolean =>
  jobs.some(
    (job) =>
      (job.status === 'queued' || job.status === 'running') &&
      String(job.tenant_id) === String(tenantId) &&
      job.kind !== 'video' &&
      job.kind !== 'storyboard' &&
      // An ad entity job's kind is its media type; the guard only compares it to that type's literals.
      !isAdJobMediaType(job.kind as GenerateMediaType | undefined) &&
      job.kind !== 'publish',
  );

export type CreativesCopy =
  | { id: 'new-banners' }
  | { id: 'carousels-to-publish'; n: number }
  | { id: 'generating' }
  | { id: 'banners-all-published'; n: number }
  | { id: 'banners'; n: number }
  | { id: 'preparing-banners' }
  | { id: 'no-banners' }
  | { id: 'see-new-banners' }
  | { id: 'edit-carousels' }
  | { id: 'open-banners' };

const OPEN_BANNERS: WebsiteCardIntent<CreativesCopy> = { copy: { id: 'open-banners' }, to: ROUTE.creatives() };

export const deriveCreativesSummary = (input: CreativesCardInput): WebsiteCardSummary<CreativesCopy> => {
  const { carouselsOn, pool, carousels, generating } = input;
  const chip = generating ? { tone: 'working' as const, copy: { id: 'generating' as const } } : null;
  // Nothing is said until every read is in: a line that changes once the last carousel lands would jump.
  const only = (body: WebsiteCardBody<CreativesCopy>): WebsiteCardSummary<CreativesCopy> => ({
    chip,
    body,
    primary: OPEN_BANNERS,
    secondary: null,
  });

  if (pool.status === 'loading' || carousels.some((carousel) => carousel.status === 'loading')) {
    return only({ kind: 'loading' });
  }
  if (pool.status === 'failed') return only({ kind: 'failed' });
  if (pool.items.length === 0) {
    return only({ kind: 'get-started', line: { id: generating ? 'preparing-banners' : 'no-banners' } });
  }

  const newBanners = pool.items.some((item) => item.hasNew);
  const toPublish = carousels.filter((carousel) => carousel.status === 'ready' && carousel.dirty);
  const lines: CreativesCopy[] = [];
  if (newBanners) lines.push({ id: 'new-banners' });
  if (toPublish.length > 0) lines.push({ id: 'carousels-to-publish', n: toPublish.length });

  const n = pool.items.length;
  // "All carousels published" is a claim about every carousel, so it needs every carousel read: one that failed, or a
  // service that is not there, withdraws it.
  const allPublished = carouselsOn && carousels.every((carousel) => carousel.status === 'ready');
  const body: WebsiteCardBody<CreativesCopy> =
    lines.length > 0
      ? { kind: 'waiting', lines }
      : { kind: 'context', line: allPublished ? { id: 'banners-all-published', n } : { id: 'banners', n } };

  const primary: WebsiteCardIntent<CreativesCopy> = newBanners
    ? { copy: { id: 'see-new-banners' }, to: ROUTE.creativesView('pool') }
    : toPublish.length > 0
      ? { copy: { id: 'edit-carousels' }, to: ROUTE.creativesView(toPublish[0].id) }
      : OPEN_BANNERS;
  const secondary: WebsiteCardIntent<CreativesCopy> | null =
    carouselsOn && primary.copy.id !== 'edit-carousels'
      ? { copy: { id: 'edit-carousels' }, to: ROUTE.creativesView('homepage') }
      : null;

  return { chip, body, primary, secondary };
};
